import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AdminAuthProvider, useAdmin } from "@/context/AdminContext";

import Login from "@/pages/Login";
import EntraCallback from "@/pages/EntraCallback";
import OAuthContinue from "@/pages/OAuthContinue";
import Profile from "@/pages/Profile";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminApps from "@/pages/admin/AdminApps";
import AdminEntra from "@/pages/admin/AdminEntra";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminConnect from "@/pages/admin/AdminConnect";
import AdminAudit from "@/pages/admin/AdminAudit";

function RequireUser({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireAdmin({ children }) {
  const { admin, loading } = useAdmin();
  if (loading) return <FullPageLoader />;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950">
      <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
    </div>
  );
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  return user ? <Navigate to="/profile" replace /> : <Navigate to="/login" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminAuthProvider>
          <Toaster theme="dark" position="top-right" />
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/entra/callback" element={<EntraCallback />} />
            <Route path="/oauth/continue" element={<RequireUser><OAuthContinue /></RequireUser>} />
            <Route path="/profile" element={<RequireUser><Profile /></RequireUser>} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/connect" element={<AdminConnect />} />
            <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
              <Route index element={<AdminOverview />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="apps" element={<AdminApps />} />
              <Route path="entra" element={<AdminEntra />} />
              <Route path="email" element={<AdminEmail />} />
              <Route path="audit" element={<AdminAudit />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AdminAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
